import { useMany, useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { theme } from 'antd';
import type { BannerActionDetailOut, BannerOut } from '@/api/generated/schemas';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { BulkModStatusControl } from '@/components/user_catalog/BulkModStatusControl';
import { buildPreviewModel } from '@/components/user_catalog/banners/bannerPreview';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import { BANNER_DEFAULT_TAB, BANNER_SHOW_TABS } from '../constants';
import { BannerShowHeader } from './BannerShowHeader';
import { BannerGeneralTab } from './BannerGeneralTab';

export const BannerShow = () => {
  const { token } = theme.useToken();
  const { query } = useShow<BannerOut>({
    resource: 'user_catalog.banners',
  });
  const { data, isLoading } = query;
  const record = data?.data;

  useRecordDocumentTitle('user_catalog.banners', record?.name);

  const [activeTab, onTabChange] = useShowTab(
    BANNER_SHOW_TABS,
    BANNER_DEFAULT_TAB
  );

  const bannerActionIds =
    record?.banner_links?.map((l) => l.banner_action_id) ?? [];

  const { result: bannerActionsResult, query: bannerActionsQuery } =
    useMany<BannerActionDetailOut>({
      resource: 'user_catalog.banner_actions',
      ids: bannerActionIds,
      queryOptions: { enabled: bannerActionIds.length > 0 },
    });

  // The model cannot tell "no creatives" from "creatives not fetched
  // yet" — without this the panel flashes its empty state on load.
  const actionsLoading =
    bannerActionIds.length > 0 && bannerActionsQuery.isLoading;

  const bannerActionsMap = new Map<number, BannerActionDetailOut>(
    (bannerActionsResult.data ?? []).map((a: BannerActionDetailOut) => [
      a.id,
      a,
    ])
  );

  const previewModel = buildPreviewModel(
    record?.banner_links,
    bannerActionsMap
  );

  return (
    <Show
      isLoading={isLoading}
      contentProps={bareShowContentProps}
      headerButtons={({ defaultButtons }) => (
        <>
          {record?.id != null && (
            <BulkModStatusControl
              resource="user_catalog.banners"
              ids={[record.id]}
              labelOf={() => record.name}
              onSuccess={() => query.refetch()}
            />
          )}
          {defaultButtons}
        </>
      )}
    >
      {record && (
        <>
          <BannerShowHeader
            record={record}
            model={previewModel}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
          <div style={{ marginTop: token.margin }}>
            {activeTab === 'general' && (
              <BannerGeneralTab
                record={record}
                model={previewModel}
                bannerActionsMap={bannerActionsMap}
                actionsLoading={actionsLoading}
              />
            )}
          </div>
        </>
      )}
    </Show>
  );
};
